import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth';
import { useTranslation } from '@/i18n';
import { Loading } from '@/ui/components/atoms/Loading';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { ExpertiseList } from '@/ui/components/molecules/ExpertiseList';
import { useReports } from '../../hooks/useReports';
import { transformReportForCard } from '../../utils/reportTransformer';
import type { ReportsViewProps } from '../../types/reports.types';
import './ReportsView.styles.css';

export const ReportsView: React.FC<ReportsViewProps> = ({ 
  navigationParams
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    reports,
    pagination,
    isLoading,
    error,
    filters,
    updateFilter
  } = useReports({ 
    initialFilter: navigationParams?.filter 
  });


  const handleReportClick = (reportId: number | string) => {
    navigate(`/client/expertise/${reportId}`, { 
      state: { from: '/client/reports' } 
    });
  };



  if (isLoading) {
    return (
      <div className="banque-reports-container">
        <Loading 
          size="large" 
          text={t('banque.reports.loadingReports')}
          fullScreen={false}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="banque-reports-container">
        <div className="banque-reports-error">
          {error instanceof Error ? error.message : 'Une erreur est survenue'}
        </div>
      </div>
    );
  }

  // Transform reports data for ExpertiseList
  const reportListData = reports.map(report => transformReportForCard(report, t));

  return (
    <div className="banque-reports-page">
      {/* Header with title and stats */}
      <div className="banque-reports-header">
        <div className="banque-reports-header-content">
          <h1 className="banque-reports-title">
            {t('banque.reports.title')}
          </h1>
          <p className="banque-reports-subtitle">
            {t('banque.reports.subtitle')}
            {user?.fmClientId && (
              <span className="banque-reports-client-info"> • {t('banque.reports.clientId')}: {user.fmClientId}</span>
            )}
          </p>
        </div>
        
        <div className="banque-reports-actions">
          {pagination && (
            <>
              <span className="banque-reports-stat">
                {t('common.total')}: <strong>{pagination.total_items}</strong> {pagination.total_items > 1 ? t('banque.reports.reports') : t('banque.reports.report')}
              </span>
              <span className="banque-reports-stat">
                {t('common.page')}: <strong>{pagination.current_page}/{pagination.total_pages}</strong>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Reports list and controls */}
      <div className="banque-reports-content">
        <ControlsContainer
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
          sortBy={filters.sortBy}
          onSortChange={(value) => updateFilter('sortBy', value as 'id' | 'progress' | 'date' | 'amount')}
          sortOrder={filters.sortOrder}
          onSortOrderChange={(value) => updateFilter('sortOrder', value as 'asc' | 'desc')}
          sortOptions={[
            { value: 'progress', label: t('banque.reports.sortBy.progress') },
            { value: 'date', label: t('banque.reports.sortBy.date') },
            { value: 'amount', label: t('banque.reports.sortBy.amount') },
            { value: 'id', label: t('banque.reports.sortBy.id') }
          ]}
          activeFilter={filters.activeFilter}
          onFilterChange={(value) => updateFilter('activeFilter', value as 'all' | 'paid' | 'unpaid')}
          filterOptions={[
            { key: 'all', label: t('common.all') },
            { key: 'paid', label: t('common.paid') },
            { key: 'unpaid', label: t('common.unpaid') }
          ]}
          showFilters={true}
          resultsCount={reports.length}
          searchPlaceholder={t('banque.reports.searchPlaceholder')}
          className="banque-reports-controls-container"
        />
        <ExpertiseList
          expertises={reportListData}
          onExpertiseClick={(report) => handleReportClick(report.id)}
          showFacadeImage={true}
          showProgress={true}
          showActions={true}
          showUrgency={false}
          showFileMaker={false}
          showTaskButton={false}
          emptyStateTitle={t('banque.reports.noReports')}
          emptyStateMessage={
            filters.searchQuery || filters.activeFilter !== 'all'
              ? t('banque.reports.noMatchingReports')
              : t('banque.reports.noReportsMessage')
          }
          isSearchActive={!!filters.searchQuery || filters.activeFilter !== 'all'}
          className="banque-reports-list"
        />
      </div>
    </div>
  );
};